

export interface ANCZoneCity {
  city: string;
  latitude: number;
  longitude: number;
  gmtWinter?: number;
  gmtSummer?: number;
}

export interface ANCCountryZone {
  country: string;
  zone: ANCZoneCity[];
}

export interface ANCModelResolved {
  modelCode: string;      // "51"
  modelName: string;      // "ANC EU0"
  version: string;        // "1.4.8"
  description: string;
  languages: string[];
  countries: ANCCountryZone[];
}


import { Injectable } from '@angular/core';

import { ANCModels } from './../../../assets/data/ANCModels';
import { ANCNames } from './../../../assets/data/ANCModels';
import { ANCCountries } from './../../../assets/data/ANCCountries';



@Injectable({ providedIn: 'root' })
export class AncDataService {

  getModelData(modelCode: string): ANCModelResolved | null {
    const model = ANCModels.find(
      m => String(m.Modelo).toUpperCase() === String(modelCode).toUpperCase()
    );

    if (!model) return null;

    const nameEntry = ANCNames.find(n => n.name === model.name);

    const resolvedCountries: ANCCountryZone[] = (nameEntry?.countries || [])
      .map((countryName) => ANCCountries.find((c) => c.country === countryName))
      .filter((c): c is ANCCountryZone => Boolean(c));

    return {
      modelCode: model.Modelo,
      modelName: model.name,
      version: model.Ver,
      description: model.description,
      languages: nameEntry?.languages || [],
      countries: resolvedCountries
    };
  }

  getLocation(
    modelCode: number | string,
    countryIndex: number,
    cityIndex: number
  ): { latitude: number; longitude: number; gmtWinter: number | null; gmtSummer: number | null; gmtWinterMinutes: number | null; gmtSummerMinutes: number | null } | null {
    const model = this.getModelData(String(modelCode));
    if (!model) return null;

    const countryIdx = Number(countryIndex);
    const cityIdx = Number(cityIndex);

    if (!Number.isFinite(countryIdx) || !Number.isFinite(cityIdx)) return null;
    if (countryIdx < 0 || cityIdx < 0) return null;

    const selectedCountry = model.countries?.[countryIdx];
    const selectedCity = selectedCountry?.zone?.[cityIdx];

    if (!selectedCity) return null;

    const gmtWinterHours = Number.isFinite(Number((selectedCity as any).gmtWinter)) ? Number((selectedCity as any).gmtWinter) : null;
    const gmtSummerHours = Number.isFinite(Number((selectedCity as any).gmtSummer)) ? Number((selectedCity as any).gmtSummer) : null;

    return {
      latitude: selectedCity.latitude,
      longitude: selectedCity.longitude,
      gmtWinter: gmtWinterHours,
      gmtSummer: gmtSummerHours,
      gmtWinterMinutes: gmtWinterHours == null ? null : gmtWinterHours * 60,
      gmtSummerMinutes: gmtSummerHours == null ? null : gmtSummerHours * 60,
    };
  }

  getCountryCityNames(
    modelCode: number | string,
    countryIndex: number,
    cityIndex: number
  ): { country: string; city: string } | null {
    const model = this.getModelData(String(modelCode));
    if (!model) return null;

    const countryIdx = Number(countryIndex);
    const cityIdx = Number(cityIndex);

    if (!Number.isFinite(countryIdx) || !Number.isFinite(cityIdx)) return null;
    if (countryIdx < 0 || cityIdx < 0) return null;

    const selectedCountry = model.countries?.[countryIdx];
    const selectedCity = selectedCountry?.zone?.[cityIdx];

    if (!selectedCountry || !selectedCity) return null;

    return {
      country: selectedCountry.country,
      city: selectedCity.city,
    };
  }

  formatProgramData(programData: any): any {
    const programs: any[] = [];
    if (!programData || typeof programData !== 'object') return programs;

    const parseArray = (raw: any): number[] | null => {
      if (raw == null) return null;
      if (typeof raw !== 'string') return null;

      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed.map((v) => Number(v));
      } catch {
        return null;
      }
    };

    const numericKeys = Object.keys(programData).filter((k) => /^\d+$/.test(k));
    numericKeys.sort((a, b) => Number(a) - Number(b));

    numericKeys.forEach((key) => {
      const parsed = parseArray(programData[key]);
      if (!parsed || parsed.length < 10) return;

      const id = Number(parsed[4]);
      if (!Number.isFinite(id) || id > 39) return;

      const time =
        ((Number(parsed[0]) & 0xff) << 24) |
        ((Number(parsed[1]) & 0xff) << 16) |
        ((Number(parsed[2]) & 0xff) << 8) |
        (Number(parsed[3]) & 0xff);

      programs.push({
        id,
        channel: parsed[5],
        type: parsed[6],
        mode: parsed[7],
        // daymask: bit6=Lunes .. bit0=Domingo
        daymask: (Number(parsed[8]) || 0) & 0x7f,
        festive: parsed[9],
        time,
      });
    });

    return programs;
  }

  //   programData.forEach((position: any) => {
  //     const program: any = {};

  //     program.id = position.program[4];
  //     program.channel = position.program[5];
  //     program.type = position.program[6];
  //     program.mode = position.program[7];
  //     program.daymask = position.program[8];
  //     program.festive = position.program[9];

  //     program.time =
  //       (position.program[0] << 24) |
  //       (position.program[1] << 16) |
  //       (position.program[2] << 8) |
  //       position.program[3];


  //     if (program.id <= 39) programs.push(program);
  //   })

  //   return programs;
  // }


  formatHolidayData(holidayData: any): any {

    if (!holidayData) return { enable: 0, periods: [] };

    const formatHoliday: { enable: number; periods: any[] } = {
      enable: Number(holidayData.enable) || 0,
      periods: [],
    };

    const pad2 = (value: any) => String(Number(value) || 0).padStart(2, '0');

    for (let i = 1; i <= 4; i++) {
      const raw = holidayData[`period${i}`];
      if (raw == null) continue;

      let parsed: any;
      try {
        parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        continue;
      }

      if (!Array.isArray(parsed) || parsed.length < 8) continue;

      const startDay = parsed[0];
      const startMonth = parsed[1];
      const startHour = parsed[2];
      const startMinute = parsed[3];

      const endDay = parsed[4];
      const endMonth = parsed[5];
      const endHour = parsed[6];
      const endMinute = parsed[7];
      const enable = parsed[8];


      formatHoliday.periods.push({
        period: i,
        startDay: startDay,
        startMonth: startMonth,
        startHour: startHour,
        startMinute: startMinute,
        startHourStr: this.formatTime(startHour, startMinute),
        startDayStr: `${pad2(startDay)}/${pad2(startMonth)} ${this.formatTime(startHour, startMinute)}`,
        endDay: endDay,
        endMonth: endMonth,
        endHour: endHour,
        endMinute: endMinute,
        endHourStr: this.formatTime(endHour, endMinute),
        endDayStr: `${pad2(endDay)}/${pad2(endMonth)} ${this.formatTime(endHour, endMinute)}`,
        enable: enable,
      });
    }

    return formatHoliday;
  }

  isTodayInAnyPeriod(
    periods: Array<{
      period?: number;
      enable?: number | boolean;
      startDay: number;
      startMonth: number;
      startHour: number | string;
      startMinute?: number | string;
      startHourStr?: string;
      endDay: number;
      endMonth: number;
      endHour: number | string;
      endMinute?: number | string;
      endHourStr?: string;
    }> | null | undefined,
    now = new Date()
  ): { inPeriod: boolean; periodIndex: number | null } {
    const getNumericValue = (value: any): number | null => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const parseTimeParts = (hourValue: any, minuteValue?: any, hourText?: any): { h: number; m: number } | null => {
      const numericHour = getNumericValue(hourValue);
      const numericMinute = getNumericValue(minuteValue);

      if (numericHour != null && numericMinute != null) {
        if (numericHour < 0 || numericHour > 23 || numericMinute < 0 || numericMinute > 59) return null;
        return { h: numericHour, m: numericMinute };
      }

      const text = typeof hourText === 'string' && hourText.includes(':')
        ? hourText
        : (typeof hourValue === 'string' && hourValue.includes(':') ? hourValue : null);

      if (!text) return null;

      const [hhRaw, mmRaw] = text.split(':');
      const h = Number(hhRaw);
      const m = Number(mmRaw);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      if (h < 0 || h > 23 || m < 0 || m > 59) return null;
      return { h, m };
    };

    const isValidMonthDay = (year: number, month: number, day: number): boolean => {
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false;
      if (month < 1 || month > 12 || day < 1 || day > 31) return false;

      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && date.getMonth() === (month - 1) && date.getDate() === day;
    };

    const buildDate = (year: number, month: number, day: number, time: { h: number; m: number }): Date | null => {
      if (!isValidMonthDay(year, month, day)) return null;
      return new Date(year, month - 1, day, time.h, time.m, 0, 0);
    };

    const buildRangeForYear = (period: any, year: number): { start: Date; end: Date } | null => {
      const sd = Number(period?.startDay);
      const sm = Number(period?.startMonth);
      const ed = Number(period?.endDay);
      const em = Number(period?.endMonth);

      if ([sd, sm, ed, em].some((value) => !Number.isFinite(value))) return null;

      const startTime = parseTimeParts(period?.startHour, period?.startMinute, period?.startHourStr);
      const endTime = parseTimeParts(period?.endHour, period?.endMinute, period?.endHourStr);
      if (!startTime || !endTime) return null;

      const start = buildDate(year, sm, sd, startTime);
      if (!start) return null;

      let endYear = year;
      if (em < sm || (em === sm && (ed < sd || (ed === sd && (endTime.h < startTime.h || (endTime.h === startTime.h && endTime.m < startTime.m)))))) {
        endYear = year + 1;
      }

      const end = buildDate(endYear, em, ed, endTime);
      if (!end || end.getTime() < start.getTime()) return null;

      return { start, end };
    };

    for (let idx = 0; idx < (periods?.length ?? 0); idx++) {
      const p = periods?.[idx];
      if (!p) continue;

      if ('enable' in p && Number(p.enable) !== 1) continue;

      const ranges = [
        buildRangeForYear(p, now.getFullYear() - 1),
        buildRangeForYear(p, now.getFullYear()),
        buildRangeForYear(p, now.getFullYear() + 1),
      ];

      if (ranges.some((range) => range && now >= range.start && now <= range.end)) {
        const periodIndex = Number.isFinite(Number(p.period)) ? Number(p.period) : (idx + 1);
        return { inPeriod: true, periodIndex };
      }
    }

    return { inPeriod: false, periodIndex: null };
  }

  formatTime(hour: number, minute: number): string {
    return ('0' + hour).slice(-2) + ':' + ('0' + minute).slice(-2);
  }


  formatSeasonData(cfg: any): {
    enable: number;
    winter: any | null;
    summer: any | null;
    date_build?: any;
    timestamp?: any;
  } {
    if (!cfg) {
      return { enable: 0, winter: null, summer: null };
    }

    const parseSeasonArray = (raw: any): any[] | null => {
      if (raw == null) return null;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : null;
        } catch {
          return null;
        }
      }
      return null;
    };

    const winterArr = parseSeasonArray(cfg.winter);
    const summerArr = parseSeasonArray(cfg.summer);

    const isSeasonArrayEnabled = (season: any[] | null): boolean =>
      Array.isArray(season) && season.length > 9 && Number(season[9]) === 1;

    let enable: number;
    if (cfg.enable !== undefined && cfg.enable !== null) {
      enable = Number(cfg.enable) ? 1 : 0;
    } else {
      enable = (isSeasonArrayEnabled(winterArr) || isSeasonArrayEnabled(summerArr)) ? 1 : 0;
    }

    const formatSeasonArray = (season: any[] | null): any | null => {
      if (!season || season.length < 8) return null;

      const nowYear = new Date().getFullYear();
      const startDay = Number(season[0]);
      const startMonth = Number(season[1]);
      const startHour = Number(season[2]);
      const startMin = Number(season[3]);
      const endDay = Number(season[4]);
      const endMonth = Number(season[5]);
      const endHour = Number(season[6]);
      const endMin = Number(season[7]);

      return {
        startDay: startDay,
        startMonth: startMonth,
        startHour: startHour,
        startMin: startMin,
        startDate: this.toIsoDate(nowYear, startMonth, startDay),
        startTime: this.toTime(startHour, startMin),
        startDateStr: `${this.pad2(startDay)}/${this.pad2(startMonth)}/${nowYear}`,
        startDateTimeStr: `${this.pad2(startDay)}/${this.pad2(startMonth)}/${nowYear} ${this.toTime(startHour, startMin)}`,
        endDay: endDay,
        endMonth: endMonth,
        endHour: endHour,
        endMin: endMin,
        endDate: this.toIsoDate(nowYear, endMonth, endDay),
        endTime: this.toTime(endHour, endMin),
        endDateStr: `${this.pad2(endDay)}/${this.pad2(endMonth)}/${nowYear}`,
        endDateTimeStr: `${this.pad2(endDay)}/${this.pad2(endMonth)}/${nowYear} ${this.toTime(endHour, endMin)}`,
      };
    };

    return {
      enable,
      winter: formatSeasonArray(winterArr),
      summer: formatSeasonArray(summerArr),
      date_build: cfg.date_build,
      timestamp: cfg.timestamp,
    };
  }


  private pad2(value: number): string {
    return String(value).padStart(2, '0');
  }

  private toIsoDate(year: number, month: number, day: number): string {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return '';
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }
    return `${year}-${this.pad2(month)}-${this.pad2(day)}`;
  }

  private toTime(hour: number, minute: number): string {
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return '';
    }
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return '';
    }
    return `${this.pad2(hour)}:${this.pad2(minute)}`;
  }

  formatCalendarStatus(stat: any){
    if (!stat) return null;
    // stat = JSON.parse(stat);
    const parsed = {
      raw : stat,
      hour: stat[0],
      minute: stat[1],
      seconds: stat[2],
      weekday: stat[3],
      day: stat[4],
      month: stat[5],
      year: stat[6],
      dateStr : `${this.pad2(stat[4])}/${this.pad2(stat[5])}/${stat[6]}`,
      // timeStr : `${this.pad2(stat[0])}:${this.pad2(stat[1])}:${this.pad2(stat[2])}`,
      timeStr : `${this.pad2(stat[0])}:${this.pad2(stat[1])}`,
      dateTimeStr : `${this.pad2(stat[4])}/${this.pad2(stat[5])}/${stat[6]} ${this.pad2(stat[0])}:${this.pad2(stat[1])}:${this.pad2(stat[2])}`,
    }
    return parsed;


  }

  /**
   * Devuelve el offset GMT (en minutos) aplicable a una fecha concreta,
   * teniendo en cuenta si el cambio de estación está activo y las fechas
   * configuradas de paso a verano / invierno.
   */
  getGmtMinutesForDate(
    date: Date,
    coords: {
      gmtWinterMinutes?: number | null;
      gmtSummerMinutes?: number | null;
    } | null,
    seasonsInfo: {
      enable?: number;
      summer?: {
        startDay: number;
        startMonth: number;
        startHour: number;
        startMin: number;
      } | null;
      winter?: {
        startDay: number;
        startMonth: number;
        startHour: number;
        startMin: number;
      } | null;
    } | null
  ): number {
    const winterMinutes = coords?.gmtWinterMinutes ?? 0;
    const summerMinutes = coords?.gmtSummerMinutes ?? winterMinutes;

    if (!seasonsInfo?.enable) {
      return winterMinutes;
    }

    const summerStart = this.buildSeasonTransitionDate(date.getFullYear(), seasonsInfo.summer);
    const winterStart = this.buildSeasonTransitionDate(date.getFullYear(), seasonsInfo.winter);

    if (!summerStart || !winterStart) {
      return winterMinutes;
    }

    const probe = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);

    return this.isDateInSummerTime(probe, summerStart, winterStart) ? summerMinutes : winterMinutes;
  }

  private buildSeasonTransitionDate(
    year: number,
    season: {
      startDay: number;
      startMonth: number;
      startHour: number;
      startMin: number;
    } | null
  ): Date | null {
    if (!season) {
      return null;
    }

    const { startDay, startMonth, startHour, startMin } = season;
    if (
      !Number.isFinite(startDay) ||
      !Number.isFinite(startMonth) ||
      !Number.isFinite(startHour) ||
      !Number.isFinite(startMin)
    ) {
      return null;
    }

    return new Date(year, startMonth - 1, startDay, startHour, startMin);
  }

  private isDateInSummerTime(probe: Date, summerStart: Date, winterStart: Date): boolean {
    if (summerStart.getTime() === winterStart.getTime()) {
      return false;
    }

    if (summerStart < winterStart) {
      return probe >= summerStart && probe < winterStart;
    }

    return probe >= summerStart || probe < winterStart;
  }

}
